import dayjs from 'dayjs';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  startAfter,
  updateDoc,
  where
} from 'firebase/firestore';

import { status } from '../../../../constants/enum';
import { throwError } from '../../../../modules/error';
import { deleteFile, uploadFile } from '../../../aws/s3';
import { db } from '../../config';
import { getUserInfo, usersRef } from '../user';

import type { Issue } from './issueType';
import type { Dayjs } from 'dayjs';
import type { User } from 'firebase/auth';
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  QueryDocumentSnapshot,
  DocumentSnapshot
} from 'firebase/firestore';

interface UserInfo {
  name: string;
  companyId: string;
}

export const issuesRef = collection(db, 'issues');

export const issueDocument = (
  issueId: string
): DocumentReference<DocumentData> => doc(issuesRef, issueId);

export const issueSnapshot = async (
  issueId: string
): Promise<DocumentSnapshot> => await getDoc(issueDocument(issueId));

export const favoriteIssuesRef = (
  mediaId: string
): CollectionReference<DocumentData> =>
  collection(db, 'issues', mediaId, 'favoriteIssue');

export interface timestamp {
  nanoseconds: number;
  seconds: number;
  start: {
    nanoseconds: number;
    seconds: number;
  };
  end: {
    nanoseconds: number;
    seconds: number;
  };
}

const checkboxArray = (value: object[]): number[] => {
  const array: number[] = [];
  value.forEach((elm: object, key) => {
    if (elm) {
      array.push(key);
    }
  });
  return array;
};

export const submitIssue = async (
  uid: string | null,
  watch: (arg0: string) => any,
  issueId?: string
): Promise<DocumentReference<DocumentData>> => {
  const q = query(usersRef, where('uid', '==', uid));

  const querySnapshot = await getDocs(q);
  let agentId, companyId;
  // 代理店が与件を登録する場合
  if (watch('client') !== 0 && watch('client') !== undefined) {
    // 広告主IDはセレクトボックスを参照
    companyId = watch('client') as string;
    querySnapshot.docs.forEach(
      (document: QueryDocumentSnapshot<DocumentData>): void => {
        // 代理店IDはcurrentUserのcompanyIdを参照
        agentId = document.data().companyId as string;
      }
    );
    //  広告主が与件を登録する場合
  } else {
    querySnapshot.docs.forEach(
      (document: QueryDocumentSnapshot<DocumentData>): void => {
        // CurrentUserのcompanyIdを参照
        companyId = document.data().companyId as string;
      }
    );
    agentId = '';
  }

  const issueData = {
    agentId,
    applicationDate: {
      end:
        watch('applicationDateTo') === null
          ? null
          : dayjs(watch('applicationDateTo') as Dayjs).toDate(),
      start:
        watch('applicationDateFrom') === null
          ? null
          : dayjs(watch('applicationDateFrom') as Dayjs).toDate()
    },
    assigneeId: uid,
    budget: (watch('budget') as number) || null,
    castingFlg:
      (watch('castingFlg') as string | boolean | null) === null
        ? null
        : (watch('castingFlg') as string | boolean | null) === 'true'
        ? true
        : (watch('castingFlg') as string | boolean | null) === 'false'
        ? false
        : (watch('castingFlg') as string | boolean | null),
    companyId,
    genre: watch('genre') as number,
    genreOther: (watch('genreOther') as number) || null,
    kpi: checkboxArray(watch('kpi') as object[]),
    kpiOther: (watch('kpiOtherText') as string) || null,
    materialFlg:
      (watch('materialFlg') as string | boolean | null) === null
        ? null
        : (watch('materialFlg') as string | boolean | null) === 'true'
        ? true
        : (watch('materialFlg') as string | boolean | null) === 'false'
        ? false
        : (watch('materialFlg') as string | boolean | null),
    mediaType:
      checkboxArray(watch('mediaType') as object[]).length > 0
        ? checkboxArray(watch('mediaType') as object[])
        : (watch('mediaTypeCheck.1') as boolean)
        ? (watch('mediaTypeCheck.1') as boolean)
        : null,
    menu:
      checkboxArray(watch('menu') as object[]).length === 0
        ? (watch('menuCheck.1') as boolean)
        : checkboxArray(watch('menu') as object[]),
    menuOther: (watch('menuOther') as string) || null,
    merchandiseType:
      (watch('merchandiseType') as string | boolean | null) === null
        ? null
        : (watch('merchandiseType') as string | boolean | null) === 'true'
        ? true
        : (watch('merchandiseType') as string | boolean | null) === 'false'
        ? false
        : (watch('merchandiseType') as string | boolean | null),
    name: (watch('name') as string) || null,
    option: checkboxArray(watch('option') as object[]),
    productInformation: (watch('productInformation') as string) || null,
    purpose: [
      watch('purpose1') as number,
      (watch('purpose2') as number) || null,
      (watch('purpose3') as number) || null
    ],
    purposeOther:
      (watch('purpose1Other') as string) ||
      (watch('purpose2Other') as string) ||
      (watch('purpose3Other') as string) ||
      null,
    startDate:
      watch('startDate') === null
        ? null
        : dayjs(watch('startDate') as Dayjs).toDate(),
    targetArea: (watch('targetArea') as string) || null,
    targetClient: (watch('targetClient') as string) || null,
    updated: new Date()
  };

  const createdData = {
    created: new Date(),
    status: status.notChecked
  };

  // IssueIdがある場合は編集、issueIdがない場合は追加
  if (issueId) {
    const issueDoc = doc(issuesRef, issueId);
    await updateDoc(issueDoc, issueData);
    return issueDoc;
  }
  // 新規登録時のみcreated/statusを入れる
  const newIssueData = Object.assign(issueData, createdData);
  const addIssueDoc = await addDoc(issuesRef, newIssueData);
  const issueDoc = doc(issuesRef, addIssueDoc.id);
  // Issueのドキュメントのidを追加
  await updateDoc(issueDoc, {
    issueId: issueDoc.id
  });
  return doc(db, 'issues', issueDoc.id);
};

export const clientOrientation = async (
  uid: string | null,
  watch: (arg0: string) => any,
  id?: string | null,
  files: Array<FileList | string> = [],
  deleteFiles: string[] = [],
  specFiles: Array<FileList | string> = [],
  specDeleteFiles: string[] = [],
  propsalFiles: Array<FileList | string> = [],
  propsalDeleteFiles: string[] = []
): Promise<void> => {
  let issueId = '';
  if (id) {
    issueId = id;
  } else {
    // 初回登録の時、uidから該当issueを検索
    const q = query(issuesRef, where('assigneeId', '==', uid));
    const querySnapshot = await getDocs(q);
    querySnapshot.docs.forEach(
      (document: QueryDocumentSnapshot<DocumentData>): void => {
        issueId = document.data().issueId as string;
      }
    );
  }

  const imageNames: Array<string | null> = [];
  const specFileNames: Array<string | null> = [];
  const proposalFileNames: Array<string | null> = [];

  // 登録済み画像の場合は、filesにS3のフォルダ名が入っているので、そのままimageNamesに入れる
  if (issueId) {
    files.map((file) => {
      if (typeof file === 'string') {
        imageNames.push(file);
        return imageNames;
      }
      const fileName = file !== null ? file[0].name : '';
      const image =
        fileName !== ''
          ? `issues/${issueId}/image/${Date.now()}_${fileName}`
          : null;
      imageNames.push(image);
      return image !== null && uploadFile(file, image);
    });
    specFiles.map((file) => {
      if (typeof file === 'string') {
        specFileNames.push(file);
        return specFileNames;
      }
      const fileName = file !== null ? file[0].name : '';
      const image =
        fileName !== ''
          ? `issues/${issueId}/image/${Date.now()}_${fileName}`
          : null;
      specFileNames.push(image);
      return image !== null && uploadFile(file, image);
    });
    propsalFiles.map((file) => {
      if (typeof file === 'string') {
        proposalFileNames.push(file);
        return proposalFileNames;
      }
      const fileName = file !== null ? file[0].name : '';
      const image =
        fileName !== ''
          ? `issues/${issueId}/image/${Date.now()}_${fileName}`
          : null;
      proposalFileNames.push(image);
      return image !== null && uploadFile(file, image);
    });
  }

  if (issueId) {
    deleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
    specDeleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
    propsalDeleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
  }

  const orientationData = {
    appealPoint: (watch('appealPoint') as string) || null,
    articleContent: checkboxArray(watch('article') as object[]),
    articleContentOther: (watch('articleOther') as string) || null,
    imageId: imageNames,
    launchDate:
      watch('launchDate') === null
        ? null
        : dayjs(watch('launchDate') as Dayjs).toDate(),
    ngContent: checkboxArray(watch('attention') as object[]) || null,
    ngContentOther: (watch('attentionOther') as string) || null,
    price: (watch('price') as string) || null,
    productExplain: (watch('productExplain') as string) || null,
    productName: (watch('product') as string) || null,
    productUrl: (watch('productUrl') as string) || null,
    proposalContent: checkboxArray(watch('proposal') as object[]),
    proposalContentOther: (watch('proposalOther') as string) || null,
    proposalFile: proposalFileNames,
    reputation: (watch('reputation') as string) || null,
    salesChannel: (watch('salesChannel') as string) || null,
    specFile: specFileNames,
    updated: new Date()
  };

  const issueDoc = doc(issuesRef, issueId);
  await updateDoc(issueDoc, orientationData);
};

export const getIssue = async (companyId: string): Promise<DocumentData[]> => {
  const q = query(
    issuesRef,
    where('companyId', '==', companyId),
    where('status', 'in', [status.approved, status.notChecked, status.fix]),
    orderBy('created', 'desc')
  );
  const issueSnap = await getDocs(q);
  const issueData: DocumentData[] = [];
  issueSnap.forEach((issueDoc) => {
    issueData.push(issueDoc.data());
  });
  return issueData;
};

export const getIssueInfiniteScrollfromAgent = async (
  issueList: Issue[],
  currentUser: User | null | undefined
): Promise<{
  docs: Array<QueryDocumentSnapshot<DocumentData>>;
  empty: boolean;
}> => {
  if (!currentUser) {
    return { docs: [], empty: true };
  }

  const resUserData = (await getUserInfo(currentUser?.uid)) as UserInfo;
  const { companyId } = resUserData;
  const lastIssue = issueList[issueList.length - 1];

  const q = !issueList.length
    ? query(
        issuesRef,
        where('agentId', '==', companyId),
        where('status', 'in', [status.approved, status.notChecked, status.fix]),
        orderBy('created', 'desc'),
        limit(50)
      )
    : query(
        issuesRef,
        where('agentId', '==', companyId),
        where('status', 'in', [status.approved, status.notChecked, status.fix]),
        orderBy('created', 'desc'),
        startAfter(lastIssue.created),
        limit(50)
      );

  const { docs, empty } = await getDocs(q);

  return { docs, empty };
};

export const getIssueInfiniteScrollfromClient = async (
  issueList: Issue[],
  currentUser: User | null | undefined
): Promise<{
  docs: Array<QueryDocumentSnapshot<DocumentData>>;
  empty: boolean;
}> => {
  if (!currentUser) {
    return { docs: [], empty: true };
  }

  const resUserData = (await getUserInfo(currentUser?.uid)) as UserInfo;
  const { companyId } = resUserData;
  const lastIssue = issueList[issueList.length - 1];

  const q = !issueList.length
    ? query(
        issuesRef,
        where('companyId', '==', companyId),
        where('status', 'in', [status.approved, status.notChecked, status.fix]),
        orderBy('created', 'desc'),
        limit(50)
      )
    : query(
        issuesRef,
        where('companyId', '==', companyId),
        where('status', 'in', [status.approved, status.notChecked, status.fix]),
        orderBy('created', 'desc'),
        startAfter(lastIssue.created),
        limit(50)
      );

  const { docs, empty } = await getDocs(q);

  return { docs, empty };
};

export const getIssueInfiniteScrollfromPublisher = async (
  issueList: Issue[]
): Promise<{
  docs: Array<QueryDocumentSnapshot<DocumentData>>;
  empty: boolean;
}> => {
  const lastIssue = issueList[issueList.length - 1];

  const q = !issueList.length
    ? query(
        issuesRef,
        where('status', 'in', [status.approved]),
        orderBy('created', 'desc'),
        limit(50)
      )
    : query(
        issuesRef,
        where('status', 'in', [status.approved]),
        orderBy('created', 'desc'),
        startAfter(lastIssue.created),
        limit(50)
      );

  const { docs, empty } = await getDocs(q);

  return { docs, empty };
};

export const getIssueFromAgent = async (
  companyId: string
): Promise<DocumentData[]> => {
  const q = query(
    issuesRef,
    where('agentId', '==', companyId),
    orderBy('created', 'desc')
  );
  const issueSnap = await getDocs(q);
  const issueData: DocumentData[] = [];
  issueSnap.forEach((issueDoc) => {
    issueData.push(issueDoc.data());
  });
  return issueData;
};

export const searchIssue = async (
  companyId: string,
  searchParams: {
    start: Dayjs | null;
    end: Dayjs | null;
    appStart: Dayjs | null;
    appEnd: Dayjs | null;
    client: number | string | null;
  }
): Promise<DocumentData[]> => {
  const q = query(
    issuesRef,
    where('agentId', '==', companyId),
    orderBy('created', 'desc')
  );

  const issueSnap = await getDocs(q);
  let issueData: DocumentData[] = [];
  issueSnap.forEach((issueDoc) => {
    issueData.push(issueDoc.data());
  });
  if (searchParams.end) {
    issueData = issueData.filter(
      (value: DocumentData) =>
        searchParams.end &&
        dayjs
          .unix((value.startDate as timestamp)?.seconds)
          .format('YYYY/MM/DD') <= searchParams.end.format('YYYY/MM/DD')
    );
  }
  if (searchParams.start) {
    issueData = issueData.filter(
      (value: DocumentData) =>
        searchParams.start &&
        searchParams.start.format('YYYY/MM/DD') <=
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD')
    );
  }
  if (searchParams.appStart) {
    issueData = issueData.filter(
      (value: DocumentData) =>
        searchParams.appStart &&
        searchParams.appStart.format('YYYY/MM/DD') <=
          dayjs
            .unix((value.applicationDate as timestamp).start?.seconds)
            .format('YYYY/MM/DD')
    );
  }
  if (searchParams.appEnd) {
    issueData = issueData.filter(
      (value: DocumentData) =>
        searchParams.appEnd &&
        dayjs
          .unix((value.applicationDate as timestamp).end?.seconds)
          .format('YYYY/MM/DD') <= searchParams.appEnd.format('YYYY/MM/DD')
    );
  }
  if (searchParams.client && searchParams.client !== 0) {
    issueData = issueData.filter(
      (value: DocumentData) => value.companyId === searchParams.client
    );
  }
  return issueData;
};

export const addFavoriteIssue = async (
  uid: string,
  issueId: string,
  companyId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      const issueRef = doc(db, 'issues', issueId);
      const issueSnap = await transaction.get(issueRef);
      const issueCompanyId = issueSnap.data()?.companyId as string;

      const favoriteData = {
        companyId,
        created: new Date(),
        issueCompanyId,
        issueId,
        uid,
        updated: new Date()
      };

      const favoriteIssueRef = collection(
        db,
        'issues',
        issueId,
        'favoriteIssue'
      );
      const favoriteDoc = await addDoc(favoriteIssueRef, favoriteData);
      await updateDoc(
        doc(db, 'issues', issueId, 'favoriteIssue', favoriteDoc.id),
        {
          favoriteMediaId: favoriteDoc.id
        }
      );

      const countRef = doc(db, 'issues', issueId);
      const countSnap = await transaction.get(countRef);
      const currentCount = (countSnap.data()?.favoriteCount as number) || 0;
      const newCount = currentCount + 1;
      transaction.update(countRef, { favoriteCount: newCount });
    });
  } catch (e) {
    throwError(e);
  }
};

export const deleteFavoriteIssue = async (
  issueId: string,
  issueFavoriteId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      const path = favoriteIssuesRef(issueId);
      const favoriteRef = doc(path, issueFavoriteId);

      const countRef = doc(db, 'issues', issueId);
      const countSnap = await transaction.get(countRef);
      const currentCount = (countSnap.data()?.favoriteCount as number) || 0;
      const newCount = currentCount - 1;
      transaction.update(countRef, { favoriteCount: newCount });

      transaction.delete(favoriteRef);
    });
  } catch (e) {
    throwError(e);
  }
};

export const getIssueDetail = async (issueId: string): Promise<Issue> => {
  const issueRef = doc(issuesRef, issueId);
  const issueSnap = await getDoc(issueRef);
  return issueSnap.data() as Issue;
};
